import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Users, TrendingUp, DollarSign, Clock, CheckCircle, ArrowRight, Sparkles, UserPlus, Star } from 'lucide-react';
import { useAuthContext } from '@/lib/AuthProvider';
import { useUserProfile } from '@/context/UserProfileContext';
import { useAffiliate } from '@/context/AffiliateContext';
import { getCurrentCommissionRate } from '@/services/commissionSettingsService';
import AuthModalWrapper from '@/components/AuthModalWrapper';
import AffiliateSignupModal from '@/components/AffiliateSignupModal';
import { getAffiliateSignupSEO, validateSEOData, generateKeywords } from '@/services/seoService';

const AffiliateSignup = () => {
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [signupModalOpen, setSignupModalOpen] = useState(false);
  const [commissionRate, setCommissionRate] = useState<number>(10); // Default fallback
  
  const { user } = useAuthContext();
  const { userProfile } = useUserProfile();
  const { application, loading } = useAffiliate();
  const navigate = useNavigate();

  // Load commission rate from Firebase
  useEffect(() => {
    const loadCommissionRate = async () => {
      try {
        const rate = await getCurrentCommissionRate();
        setCommissionRate(rate);
      } catch (error) {
        console.error('Error loading commission rate:', error);
        // Keep default fallback value
      }
    };

    loadCommissionRate();
  }, []);

  // Check authentication when component mounts
  useEffect(() => {
    if (!user) {
      setAuthModalOpen(true);
    }
  }, [user]);

  // Redirect to dashboard if user is already an approved affiliate
  useEffect(() => {
    if (user && userProfile?.isAffiliate && application?.status === 'approved') {
      navigate('/affiliate');
    }
  }, [user, userProfile?.isAffiliate, application?.status, navigate]);

  const handleApplyClick = () => {
    if (!user) {
      setAuthModalOpen(true);
    } else {
      setSignupModalOpen(true);
    }
  };

  const programBenefits = [
    {
      icon: DollarSign,
      title: 'Competitive Commissions',
      description: `Earn ${commissionRate}% commission on every sale you refer`,
    },
    {
      icon: TrendingUp,
      title: 'Growing Market',
      description: 'Authentic Jamaican products with increasing demand',
    },
    {
      icon: Users,
      title: 'Marketing Support',
      description: 'Access to promotional materials and product information',
    },
    {
      icon: Clock,
      title: 'Monthly Payouts',
      description: 'Regular payments via PayPal or Jamaican bank transfer',
    },
  ];

  const requirements = [
    'Active social media presence or website',
    'Genuine interest in promoting Jamaican culture',
    'Ability to create engaging content',
    'Professional communication standards',
  ];

  // Generate SEO data for Affiliate Signup page
  const seoData = validateSEOData(getAffiliateSignupSEO());

  return (
    <>
      <Helmet>
        {/* Basic Meta Tags */}
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        {seoData.keywords && (
          <meta name="keywords" content={generateKeywords(seoData.keywords)} />
        )}
        <meta name="author" content={seoData.author} />
        
        {/* Open Graph Tags for Social Sharing */}
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:image" content={seoData.image} />
        <meta property="og:url" content={seoData.url} />
        <meta property="og:type" content={seoData.type} />
        <meta property="og:site_name" content={seoData.siteName} />
        <meta property="og:locale" content={seoData.locale} />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoData.title} />
        <meta name="twitter:description" content={seoData.description} />
        <meta name="twitter:image" content={seoData.image} />
        
        {/* Additional SEO Tags */}
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={seoData.url} />
      </Helmet>
      
      <div className="min-h-screen relative bg-gradient-to-br from-gray-50 via-white to-gray-100 overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Floating Geometric Shapes */}
        <div className="absolute top-20 left-20 w-16 h-16 bg-gradient-to-br from-jamaica-green/10 to-jamaica-green/5 rounded-2xl transform rotate-45 animate-pulse delay-300"></div>
        <div className="absolute top-40 right-32 w-12 h-12 bg-jamaica-yellow/15 rounded-full animate-bounce delay-500"></div>
        <div className="absolute bottom-32 left-1/4 w-20 h-20 bg-gradient-to-br from-jamaica-yellow/8 to-jamaica-green/8 rounded-3xl transform rotate-12 animate-ping delay-700"></div>
        <div className="absolute bottom-20 right-20 w-14 h-14 bg-jamaica-green/12 rounded-lg transform -rotate-12 animate-pulse delay-900"></div>
        
        {/* Triangle Decorative Elements */}
        <div className="absolute top-1/4 right-10 w-0 h-0 border-l-[20px] border-r-[20px] border-b-[35px] border-l-transparent border-r-transparent border-b-jamaica-green/10 animate-pulse delay-1000"></div>
        <div className="absolute bottom-1/3 left-16 w-0 h-0 border-l-[15px] border-r-[15px] border-b-[26px] border-l-transparent border-r-transparent border-b-jamaica-yellow/15 animate-bounce delay-1200"></div>
        
        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute inset-0" style={{
            backgroundImage: `
              linear-gradient(rgba(0, 116, 47, 0.05) 1px, transparent 1px),
              linear-gradient(90deg, rgba(0, 116, 47, 0.05) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }}></div>
        </div>
      </div>

      <div className="relative container mx-auto px-4 max-w-4xl py-8">
        {/* Enhanced Hero Section */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="relative">
              <div className="w-12 h-12 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-2xl flex items-center justify-center shadow-xl transform rotate-6">
                <UserPlus className="w-6 h-6 text-white" />
              </div>
              <div className="absolute -top-2 -right-2 w-5 h-5 bg-jamaica-yellow rounded-full flex items-center justify-center animate-bounce">
                <DollarSign className="w-2.5 h-2.5 text-white" />
              </div>
            </div>
            <div className="bg-gradient-to-r from-jamaica-green/10 to-jamaica-yellow/10 px-4 py-1.5 rounded-2xl border border-jamaica-green/20 shadow-lg">
              <span className="text-sm font-bold text-jamaica-black flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5 text-jamaica-green" />
                Affiliate Program
              </span>
            </div>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 leading-tight">
            <span className="bg-gradient-to-r from-gray-900 via-jamaica-green to-gray-900 bg-clip-text text-transparent p-1">
              Join the JamPack'd Affiliate Program
            </span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-6">
            Share the authentic taste of 
            <span className="text-jamaica-yellow font-semibold"> Jamaica</span> with your audience and earn
            <span className="text-jamaica-green font-semibold"> competitive commissions</span> on every sale
          </p>
        </motion.div>

        {/* Current Status Card (if user is logged in) */}
        {user && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-8"
          >
            <Card className="relative bg-white/80 backdrop-blur-sm border-0 shadow-xl overflow-hidden">
              {/* Background decoration */}
              <div className="absolute inset-0 bg-gradient-to-r from-jamaica-green/5 via-transparent to-jamaica-yellow/5"></div>
              <div className="absolute top-0 right-0 w-32 h-32 bg-jamaica-yellow/10 rounded-full -translate-y-16 translate-x-16"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-jamaica-green/10 rounded-full translate-y-12 -translate-x-12"></div>
              
              <CardHeader className="relative">
                <CardTitle className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-xl flex items-center justify-center shadow-lg">
                    <Users className="h-5 w-5 text-white" />
                  </div>
                  Application Status
                </CardTitle>
              </CardHeader>
              <CardContent className="relative">
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-jamaica-green"></div>
                  <span>Loading your application status...</span>
                </div>
              ) : application ? (
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">
                      Your application is{' '}
                      <Badge 
                        variant={
                          application.status === 'approved' ? 'default' :
                          application.status === 'pending' ? 'secondary' : 'destructive'
                        }
                        className={
                          application.status === 'approved' ? 'bg-jamaica-green' :
                          application.status === 'pending' ? 'bg-yellow-500' : ''
                        }
                      >
                        {application.status}
                      </Badge>
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      {application.status === 'pending' && 'We\'ll review your application and get back to you soon.'}
                      {application.status === 'approved' && 'Congratulations! You can now access your affiliate dashboard.'}
                      {application.status === 'rejected' && 'Your application was not approved at this time.'}
                    </p>
                  </div>
                  {application.status === 'approved' && userProfile?.isAffiliate && (
                    <Button onClick={() => navigate('/affiliate')} className="bg-jamaica-green hover:bg-jamaica-green/90">
                      Go to Dashboard
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  )}
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-gray-600 mb-4">You haven't applied to the affiliate program yet.</p>
                  <Button 
                    onClick={handleApplyClick}
                    className="bg-jamaica-green hover:bg-jamaica-green/90"
                  >
                    Apply Now
                  </Button>
                </div>
              )}
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Program Benefits */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <Card className="relative bg-white/80 backdrop-blur-sm border-0 shadow-xl overflow-hidden">
            {/* Background decoration */}
            <div className="absolute inset-0 bg-gradient-to-r from-jamaica-yellow/5 via-transparent to-jamaica-green/5"></div>
            <div className="absolute top-0 left-0 w-28 h-28 bg-jamaica-green/10 rounded-full -translate-y-14 -translate-x-14"></div>
            <div className="absolute bottom-0 right-0 w-20 h-20 bg-jamaica-yellow/10 rounded-full translate-y-10 translate-x-10"></div>
            
            <CardHeader className="relative">
              <div className="flex items-center gap-4 mb-2">
                <div className="w-12 h-12 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-2xl flex items-center justify-center shadow-lg">
                  <Star className="h-6 w-6 text-white" />
                </div>
                <div>
                  <CardTitle className="text-2xl font-bold text-gray-900">Program Benefits</CardTitle>
                  <CardDescription className="text-gray-600">
                    Why join our affiliate program?
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="relative">
              <div className="grid md:grid-cols-2 gap-6">
                {programBenefits.map((benefit, index) => (
                  <div key={index} className="flex gap-4 p-4 bg-jamaica-green/5 rounded-xl border border-jamaica-green/10">
                    <div className="flex-shrink-0">
                      <div className="h-12 w-12 bg-jamaica-green/20 rounded-xl flex items-center justify-center">
                        <benefit.icon className="h-6 w-6 text-jamaica-green" />
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                      <p className="text-gray-600 text-sm">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Requirements */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-8"
        >
          <Card className="relative bg-white/80 backdrop-blur-sm border-0 shadow-xl overflow-hidden">
            {/* Background decoration */}
            <div className="absolute inset-0 bg-gradient-to-r from-jamaica-green/5 via-transparent to-jamaica-yellow/5"></div>
            <div className="absolute top-0 right-0 w-32 h-32 bg-jamaica-yellow/10 rounded-full -translate-y-16 translate-x-16"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-jamaica-green/10 rounded-full translate-y-12 -translate-x-12"></div>
            
            <CardHeader className="relative">
              <div className="flex items-center gap-4 mb-2">
                <div className="w-12 h-12 bg-gradient-to-br from-jamaica-yellow to-amber-500 rounded-2xl flex items-center justify-center shadow-lg">
                  <CheckCircle className="h-6 w-6 text-white" />
                </div>
                <div>
                  <CardTitle className="text-2xl font-bold text-gray-900">Requirements</CardTitle>
                  <CardDescription className="text-gray-600">
                    What we look for in our affiliates
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="relative">
              <div className="space-y-4">
                {requirements.map((requirement, index) => (
                  <div key={index} className="flex items-center gap-4 p-3 bg-jamaica-green/5 rounded-lg border border-jamaica-green/10">
                    <CheckCircle className="h-5 w-5 text-jamaica-green flex-shrink-0" />
                    <span className="text-gray-700 font-medium">{requirement}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* How It Works */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-8"
        >
          <Card className="relative bg-white/80 backdrop-blur-sm border-0 shadow-xl overflow-hidden">
            {/* Background decoration */}
            <div className="absolute inset-0 bg-gradient-to-r from-jamaica-yellow/5 via-transparent to-jamaica-green/5"></div>
            <div className="absolute top-0 left-0 w-28 h-28 bg-jamaica-green/10 rounded-full -translate-y-14 -translate-x-14"></div>
            <div className="absolute bottom-0 right-0 w-20 h-20 bg-jamaica-yellow/10 rounded-full translate-y-10 translate-x-10"></div>
            
            <CardHeader className="relative">
              <div className="flex items-center gap-4 mb-2">
                <div className="w-12 h-12 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-2xl flex items-center justify-center shadow-lg">
                  <TrendingUp className="h-6 w-6 text-white" />
                </div>
                <div>
                  <CardTitle className="text-2xl font-bold text-gray-900">How It Works</CardTitle>
                  <CardDescription className="text-gray-600">
                    Simple steps to start earning
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="relative">
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center p-4 bg-jamaica-green/5 rounded-xl border border-jamaica-green/10">
                  <div className="w-12 h-12 bg-jamaica-green/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <span className="text-xl font-bold text-jamaica-green">1</span>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Apply</h3>
                  <p className="text-gray-600 text-sm">
                    Submit your application with your social profiles and payment details
                  </p>
                </div>
                <div className="text-center p-4 bg-jamaica-yellow/5 rounded-xl border border-jamaica-yellow/10">
                  <div className="w-12 h-12 bg-jamaica-yellow/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <span className="text-xl font-bold text-jamaica-green">2</span>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Get Approved</h3>
                  <p className="text-gray-600 text-sm">
                    We'll review your application and provide your unique referral link
                  </p>
                </div>
                <div className="text-center p-4 bg-jamaica-green/5 rounded-xl border border-jamaica-green/10">
                  <div className="w-12 h-12 bg-jamaica-green/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <span className="text-xl font-bold text-jamaica-green">3</span>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Start Earning</h3>
                  <p className="text-gray-600 text-sm">
                    Share your link and earn {commissionRate}% commission on every sale
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Call to Action */}
        {(!user || (!application && !loading)) && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <Card className="relative bg-gradient-to-br from-jamaica-green/5 to-jamaica-yellow/5 border-0 shadow-xl backdrop-blur-sm overflow-hidden">
              {/* Background decoration */}
              <div className="absolute inset-0 bg-gradient-to-r from-jamaica-green/5 via-transparent to-jamaica-yellow/5"></div>
              <div className="absolute top-0 right-0 w-32 h-32 bg-jamaica-yellow/10 rounded-full -translate-y-16 translate-x-16"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-jamaica-green/10 rounded-full translate-y-12 -translate-x-12"></div>
              
              <CardContent className="relative text-center py-12">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-2xl mb-6 shadow-lg">
                  <UserPlus className="h-8 w-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Ready to Get Started?</h2>
                <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                  Join our affiliate program and start earning commissions today! Share authentic Jamaican products with your audience and build your income stream.
                </p>
                <Button 
                  onClick={handleApplyClick}
                  size="lg"
                  className="bg-gradient-to-r from-jamaica-green to-jamaica-green/90 text-white hover:from-jamaica-green/90 hover:to-jamaica-green shadow-lg hover:shadow-xl transition-all duration-300 px-8 py-3 rounded-xl"
                >
                  {user ? 'Apply Now' : 'Sign Up & Apply'}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <div className="mt-6 text-sm text-gray-600">
                  {/* Mobile: compact chip grid; Desktop: original inline layout */}
                  <div className="grid grid-cols-1 gap-2 px-4 sm:hidden">
                    <div className="flex items-center gap-2 justify-center rounded-full border border-green-200 bg-green-50/60 text-gray-700 px-3 py-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="font-medium">No upfront costs</span>
                    </div>
                    <div className="flex items-center gap-2 justify-center rounded-full border border-green-200 bg-green-50/60 text-gray-700 px-3 py-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="font-medium">{commissionRate}% commission rate</span>
                    </div>
                    <div className="flex items-center gap-2 justify-center rounded-full border border-green-200 bg-green-50/60 text-gray-700 px-3 py-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="font-medium">Monthly payouts</span>
                    </div>
                  </div>

                  <div className="hidden sm:flex items-center justify-center gap-6 text-gray-500">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span>No upfront costs</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span>{commissionRate}% commission rate</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span>Monthly payouts</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>

      {/* Modals */}
      <AuthModalWrapper 
        isOpen={authModalOpen} 
        onOpenChange={setAuthModalOpen}
        defaultTab="register"
      />
      
      <AffiliateSignupModal 
        isOpen={signupModalOpen}
        onOpenChange={setSignupModalOpen}
      />
    </div>
    </>
  );
};

export default AffiliateSignup;